import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "../css/MSLPob.css";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";

const RrrRrports = () => {
  const navigate = useNavigate();

  // State hooks
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");
  const [retailerType, setRetailerType] = useState("All");
  const [activeStatus, setActiveStatus] = useState("All");
  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [financialYear, setFinancialYear] = useState("");
  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [errors, setErrors] = useState({
    state: false,
    employee: false,
  });

  // Helper to build filter state to pass to destination pages
  const buildFilterState = () => ({
    selectedState,
    selectedDivision,
    selectedEmployee,
    retailerType,
    activeStatus,
    startMonth,
    endMonth,
    financialYear,
  });

  const navigateWithState = (path, extraState = {}) => {
    navigate(path, { state: { ...buildFilterState(), ...extraState } });
  };

  // Helper functions
  const getFinancialYears = () => {
    const currentYear = new Date().getFullYear();
    let years = [];
    for (let y = 2021; y <= currentYear + 1; y++) {
      years.push(`${y}-${y + 1}`);
    }
    return years;
  };

  const fetchEmployees = useCallback(async (stateId, divisionName) => {
    if (!stateId) return;
    try {
      let url = `${BASE_URL}/api/employees?state_id=${stateId}`;
      if (divisionName && divisionName !== "All") {
        url += `&division=${encodeURIComponent(divisionName)}`;
      }
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setEmployees([
        { id: "All", first_name: "All", last_name: "" },
        ...res.data,
      ]);
      setSelectedEmployee("All");
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([]);
    }
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        setStates(response.data);
      } catch (err) {
        console.error("Error fetching states:", err);
      }
    };
    fetchStates();
  }, []);

  const handleStateChange = async (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("All");
    setSelectedEmployee("All");

    try {
      const divisionRes = await axios.get(
        `${BASE_URL}/api/divisions?state_id=${stateId}`,
        { headers: getAuthHeaders() }
      );
      setDivisions([{ id: "All", name: "All" }, ...divisionRes.data]);
    } catch (err) {
      console.error("Error loading divisions", err);
    }

    fetchEmployees(stateId, "All");
  };

  const handleDivisionChange = async (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedEmployee("All");
    const division = divisions.find((d) => d.id == divisionId);
    const divisionName = division?.name || "All";
    fetchEmployees(selectedState, divisionName);
  };

  const formatMonthForAPI = (monthString) => {
    if (!monthString) return "";
    const [year, month] = monthString.split("-");
    const date = new Date(year, month - 1);
    return date.toLocaleString("default", { month: "long" }) + " " + year;
  };

  const handleGoClick = async () => {
    const newErrors = {
      state: !selectedState,
      employee: selectedEmployee === "All" || !selectedEmployee,
    };

    setErrors(newErrors);

    if (newErrors.state || newErrors.employee) {
      return;
    }

    setShowTable(true);
    try {
      let url = `${BASE_URL}/api/combined-retailers?employee_id=${selectedEmployee}`;

      if (retailerType !== "All") {
        url += `&retailer_type=${retailerType.toLowerCase()}`;
      }

      if (activeStatus !== "All") {
        url += `&active=${activeStatus === "Yes"}`;
      }

      const formattedStartMonth = formatMonthForAPI(startMonth);
      const formattedEndMonth = formatMonthForAPI(endMonth);

      if (formattedStartMonth) {
        url += `&start_month=${encodeURIComponent(formattedStartMonth)}`;
      }
      if (formattedEndMonth) {
        url += `&end_month=${encodeURIComponent(formattedEndMonth)}`;
      }

      const response = await axios.get(url, { headers: getAuthHeaders() });
      setTableData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setTableData([]);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const day = date.getDate().toString().padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // POB Navigation Handlers
 const handleTotalPobClick = (item) => {
  const empId = item.employee_id || selectedEmployee;

  navigateWithState(`/pob-details-doctor-chemist/${empId}`, {
    title: "Total POB",
    retailerId: item.id,
    retailerName: item.name,
    viewType: "total",
    employeeId: empId,
    employeeName: item.employee_name
  });
};

const handleMonthPobClick = (item, monthKey, monthLabel) => {
  const empId = item.employee_id || selectedEmployee;

  navigateWithState(`/pob-details-doctor-chemist/${empId}`, {
    monthKey,
    monthLabel,
    retailerId: item.id,
    retailerName: item.name,
    viewType: "monthwise",
    employeeId: empId,
    employeeName: item.employee_name
  });
};

  const handleRouteClick = (item) => {
    navigateWithState(`/route-details/${item.route_id}`, {
      routeName: item.route_name,
      routeId: item.route_id,
      employeeId: item.employee_id,
      employeeName: item.employee_name
    });
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Visit/Support Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .header { text-align: center; margin-bottom: 20px; }
            .footer { text-align: center; margin-top: 20px; font-size: 12px; }
            .msl_link_button { 
              background: none;
              border: none;
              color: blue;
              text-decoration: underline;
              cursor: pointer;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Visit/Support Report</h2>
          </div>
          ${document.querySelector(".msl_pob-table-section").innerHTML}
          <div class="footer">
            Generated on: ${new Date().toLocaleString()}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleExportExcel = () => {
    if (tableData.length === 0) {
      alert("No data to export");
      return;
    }

    const worksheetData = [
      [
        "S.No",
        "Employee",
        "Type",
        "Code",
        "Retailer",
        "Active",
        "State",
        "City",
        "Area",
        "Route",
        "MOBILE",
        "Address",
        "System Added Date",
        "Latest Visit Date",
        "Prev FY Avg",
        "Curr FY Avg",
        "Total Visits",
        "Total POB",
        ...[
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
          "Jan",
          "Feb",
          "Mar",
        ].flatMap((month) => [`${month} Visits`, `${month} POB`]),
      ],
      ...tableData.map((item, index) => [
        index + 1,
        item.employee_name || "N/A",
        item.type,
        item.code,
        item.name,
        item.active ? "Yes" : "No",
        item.state_name || "",
        item.city_name || "",
        item.area_name || "",
        item.route_name || "",
        item.mobile || "",
        item.address || "",
        formatDate(item.system_added_date),
        formatDate(item.latest_visit_date),
        item.fy_avg_prev || "",
        item.fy_avg_curr || "",
        item.total_visits || 0,
        item.total_pob || 0,
        ...[
          "apr",
          "may",
          "jun",
          "jul",
          "aug",
          "sep",
          "oct",
          "nov",
          "dec",
          "jan",
          "feb",
          "mar",
        ].flatMap((month) => [
          item[`${month}_visit`] || 0,
          item[`${month}_pob`] || 0,
        ]),
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "VisitSupportReport");

    const fileName = `VisitSupportReport_${selectedState}_${selectedEmployee}_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // Month configuration
  const monthKeys = ["apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec", "jan", "feb", "mar"];
  const monthDisplay = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];

  return (
    <div className="msl_pob-main-container">
      <div className="msl_pob-filter-container">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700"> RRR </span>
          <span className="text-blue-500">Report</span>
        </h1>

        <div className="msl_pob-row1">
          <label className="msl-pob-label">State</label>
          <select
            className={`msl-pob-dropdown ${errors.state ? "error-border" : ""}`}
            onChange={(e) => {
              handleStateChange(e.target.value);
              setErrors({ ...errors, state: false });
            }}
            value={selectedState}
            required
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="msl-pob-label">Division</label>
          <select
            className="msl-pob-dropdown"
            onChange={(e) => handleDivisionChange(e.target.value)}
            value={selectedDivision}
          >
            {divisions.map((div) => (
              <option key={div.id} value={div.id}>
                {div.name}
              </option>
            ))}
          </select>
        </div>

        <div className="msl_pob-row2">
          <label className="msl-pob-label">Employee</label>
          <select
            className={`msl-pob-dropdown ${
              errors.employee ? "error-border" : ""
            }`}
            onChange={(e) => {
              setSelectedEmployee(e.target.value);
              setErrors({ ...errors, employee: false });
            }}
            value={selectedEmployee}
            required
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.id === "All"
                  ? "All"
                  : `${emp.first_name} ${emp.last_name} (${emp.designation} | ${emp.headquarter_name} | ${emp.division} | ${emp.state_name})`}
              </option>
            ))}
          </select>

          <label className="msl-pob-label">Retailer Type</label>
          <select
            className="msl-pob-dropdown"
            value={retailerType}
            onChange={(e) => setRetailerType(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Retailer">Retailer</option>
            <option value="Doctor">Doctor</option>
          </select>
        </div>

        <div className="msl_pob-row3">
          <label className="msl-pob-label">FY Start Month</label>
          <input
            className="month-input"
            type="month"
            value={startMonth}
            onChange={(e) => setStartMonth(e.target.value)}
          />

          <label className="msl-pob-label">FY End Month</label>
          <input
            className="month-input"
            type="month"
            value={endMonth}
            onChange={(e) => setEndMonth(e.target.value)}
          />
        </div>

        <div className="msl_pob-row3">
          <label className="msl-pob-label">Financial Year</label>
          <select
            className="msl-pob-dropdown"
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
          >
            {getFinancialYears().map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <label className="msl-pob-label">Active</label>
          <select
            className="msl-pob-dropdown"
            value={activeStatus}
            onChange={(e) => setActiveStatus(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>

        <div className="msl_pob-go-button-container">
          <button className="msl_pob-gobutton" onClick={handleGoClick}>
            GO
          </button>
        </div>
      </div>

      {showTable && (
        <>
          <div className="msl_pob-table-section">
            <div className="msl_pob-title3-container">
              <h2 className="msl_pob-table-title">
                {selectedEmployee !== "All"
                  ? `Employee: ${
                      employees.find((e) => e.id == selectedEmployee)
                        ?.first_name || ""
                    } ${
                      employees.find((e) => e.id == selectedEmployee)
                        ?.last_name || ""
                    }`
                  : "All Employees"}{" "}
                - Period: {formatMonthForAPI(startMonth) || "N/A"} to{" "}
                {formatMonthForAPI(endMonth) || "N/A"}
              </h2>
            </div>
            <div className="msl_pob-table-wrapper">
              <table className="msl_pob-table">
                <thead className="text-white">
                  <tr>
                    <th>S.No</th>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Code</th>
                    <th>Retailer</th>
                    <th>Active</th>
                    <th>State</th>
                    <th>City</th>
                    <th>Area</th>
                    <th>Route</th>
                    <th>MOBILE</th>
                    <th>Address</th>
                    <th>System Added Date</th>
                    <th>Latest Visit Date</th>
                    <th>Prev FY Avg</th>
                    <th>Curr FY Avg</th>
                    <th>Total Visits</th>
                    <th>Total POB</th>
                    {monthDisplay.map((month) => (
                      <React.Fragment key={month}>
                        <th>{month} Visits</th>
                        <th>{month} POB</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item, index) => (
                    <tr key={item.route_id ?? `${index}`}>
                      <td>{index + 1}</td>
                      <td>{item.employee_name || "N/A"}</td>
                      <td>{item.type}</td>
                      <td>{item.code}</td>
                      <td>{item.name}</td>
                      <td>{item.active ? "Yes" : "No"}</td>
                      <td>{item.state_name || ""}</td>
                      <td>{item.city_name || ""}</td>
                      <td>{item.area_name || ""}</td>

                      {/* Route column clickable */}
                      <td>
                        {item.route_id ? (
                          <button
                            type="button"
                            className="msl_link_button"
                            onClick={() => handleRouteClick(item)}
                          >
                            {item.route_name || "View Route"}
                          </button>
                        ) : (
                          item.route_name || ""
                        )}
                      </td>

                      <td>{item.mobile || ""}</td>
                      <td>{item.address || ""}</td>
                      <td>{formatDate(item.system_added_date)}</td>
                      <td>{formatDate(item.latest_visit_date)}</td>
                      <td>{item.fy_avg_prev || ""}</td>
                      <td>{item.fy_avg_curr || ""}</td>
                      <td>{item.total_visits || 0}</td>

                      {/* Total POB clickable */}
                      <td>
                        <button
                          type="button"
                          className="msl_link_button"
                          onClick={() => handleTotalPobClick(item)}
                        >
                          {item.total_pob || 0}
                        </button>
                      </td>

                      {/* Monthwise visits and clickable month POB */}
                      {monthKeys.map((mkey, mi) => (
                        <React.Fragment key={mkey}>
                          <td>{item[`${mkey}_visit`] || 0}</td>
                          <td>
                            <button
                              type="button"
                              className="msl_link_button"
                              onClick={() => handleMonthPobClick(item, mkey, monthDisplay[mi])}
                            >
                              {item[`${mkey}_pob`] || 0}
                            </button>
                          </td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="pob-datewise-export-buttons">
            <CustomButton onClick={handlePrint} label={"Print"} variant="print">
              🖨
            </CustomButton>

            <CustomButton
              onClick={handleExportExcel}
              label="Export to Excel"
              variant="export"
            >
              📥
            </CustomButton>
          </div>
        </>
      )}
    </div>
  );
};

export default RrrRrports;